import React, { useState } from 'react';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { Button } from '../../atoms/Button';
import { Flex } from '../../atoms/Flex';
import { Input } from '../../atoms/Input';
import { Icon } from '../../atoms/Icon';
import { TopNavLink } from '../../molecules/TopNavLink';
import { AccountButton, DropdownLink } from '../../molecules/AccountButton';
import topNavLinksData from '../../config/topNavLinks.json';
import './TopNav.css';

export interface TopNavProps {
  showSearch?: boolean;
  isLoggedIn?: boolean;
  wwwDomain?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const TopNav: React.FC<TopNavProps> = ({
  showSearch = true,
  isLoggedIn = false,
  wwwDomain = 'www.heartinternet.uk',
  className = '',
  style,
}) => {
  const { topLinks, accountDropdownLinks } = topNavLinksData;
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const protocol = 'https';

  const handleSearch = () => {
    if (searchQuery.trim()) {
      window.location.href = `${protocol}://${wwwDomain}/domain-names?domain=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  return (
    <div id="hi-ui-topnav" className={`hi-ui hi-ui-topnav ${className}`.trim()} style={style}>
      <LayoutContainer>
        <Flex direction="row" align="center" justify={showSearch ? 'between' : 'end'} gap="md">
          {/* Left side: Search */}
          {showSearch && (
            <Flex direction="row" align="center" gap="md" className="hi-ui-topnav-search">
              <Icon name="search" size={26} color="#333333" aria-label="Search" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => {
                  // Delay to allow button clicks to register
                  setTimeout(() => setIsSearchFocused(false), 200);
                }}
                onKeyDown={handleKeyDown}
                placeholder="search for a domain"
                aria-label="Search input"
              />
            </Flex>
          )}

          {/* Right side: Search controls (when focused) OR Nav links + Account/Login (when not focused) */}
          <div className="hi-ui-topnav-right-container">
            <Flex
              direction="row"
              align="center"
              gap="sm"
              className={`hi-ui-topnav-right hi-ui-topnav-search-controls ${isSearchFocused ? 'visible' : ''}`}
            >
              <Button
                variant="tertiary"
                size="medium"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur
                  handleSearch();
                }}
              >
                Search
              </Button>
              <Button
                variant="tertiary"
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur
                  handleClear();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  minWidth: 'auto',
                }}
                aria-label="Clear search"
              >
                <Icon name="cross" size={20} color="#333333" />
              </Button>
            </Flex>

            <Flex
              direction="row"
              align="center"
              gap="none"
              style={{ gap: '20px' }}
              className={`hi-ui-topnav-right hi-ui-topnav-links ${!isSearchFocused ? 'visible' : ''}`}
            >
              {/* Top navigation links */}
              {topLinks.map((link, index) => {
                // Construct full URL for presales links (unless already absolute)
                const href = link.isAbsolute ? link.href : `${protocol}://${wwwDomain}${link.href}`;
                return (
                  <TopNavLink key={index} href={href} label={link.label} external={link.external} />
                );
              })}

              {/* Account button - handles both logged in and logged out states */}
              <AccountButton
                isLoggedIn={isLoggedIn}
                loginHref="https://customer.heartinternet.uk/manage/"
                dropdownLinks={accountDropdownLinks as DropdownLink[]}
              />
            </Flex>
          </div>
        </Flex>
      </LayoutContainer>
    </div>
  );
};
